import {
  miniApp, 
  viewport, 
  swipeBehavior,
  hapticFeedback,
  retrieveLaunchParams,
  retrieveRawInitData,
  on,
  type ThemeParams
} from '@tma.js/sdk-react';
import { Platform, User } from './types.js';

export class TelegramPlatform implements Platform {
  readonly type = 'telegram';
  readonly isTelegram = true;

  get rawInitData(): string | undefined {
    return retrieveRawInitData();
  }

  async initialize() {
    // 1. Expand viewport to maximum height
    this.expand();

    // 2. Request Fullscreen for immersive experience
    await this.requestFullscreen().catch(() => {
      console.warn('Fullscreen request failed or not supported');
    });

    // 3. Disable vertical swipe to prevent accidental app closing
    if (swipeBehavior.mount.isAvailable()) {
      swipeBehavior.mount();
      if (swipeBehavior.disableVertical.isAvailable()) {
        swipeBehavior.disableVertical();
      }
    }

    // 4. Notify Telegram we are ready to hide the loading placeholder
    if (miniApp.ready.isAvailable()) {
      miniApp.ready();
    }

    // 5. Set header color to match our PlayerHud
    if (miniApp.setHeaderColor.isAvailable()) {
      miniApp.setHeaderColor('#161614'); // Цвет --card из index.css
    }
    if (miniApp.setBgColor.isAvailable()) {
      miniApp.setBgColor('#0c0c0b'); // Цвет --background из index.css
    }
  }

  get user(): User | undefined {
    const params = retrieveLaunchParams();
    const tgUser = params.tgWebAppData?.user;
    
    if (!tgUser) return undefined;

    return {
      id: tgUser.id,
      firstName: tgUser.first_name,
      lastName: tgUser.last_name,
      username: tgUser.username,
      languageCode: tgUser.language_code,
      photoUrl: tgUser.photo_url,
    };
  }

  readonly haptic = {
    impact: (style: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft') => {
      hapticFeedback.impactOccurred.ifAvailable(style);
    },
    notification: (type: 'success' | 'warning' | 'error') => {
      hapticFeedback.notificationOccurred.ifAvailable(type);
    },
    selectionChanged: () => {
      hapticFeedback.selectionChanged.ifAvailable();
    }
  };

  expand() {
    if (viewport.expand.isAvailable()) {
      viewport.expand();
    }
  }

  async requestFullscreen() {
    if (viewport.requestFullscreen.isAvailable()) {
      await viewport.requestFullscreen();
    }
  }

  applyTheme() {
    const params = retrieveLaunchParams();
    const theme = params.tgWebAppThemeParams;

    if (theme) {
      this.updateCssVars(theme as unknown as Record<string, string>);
    }

    // Subscribe to theme changes
    on('theme_changed', ({ theme_params }) => {
      this.updateCssVars(theme_params as Record<string, string>);
    });

    // Handle Safe Areas and App Height
    this.updateViewportVars();

    // Subscribe to all layout-affecting signals
    viewport.height.sub(() => this.updateViewportVars());
    viewport.safeAreaInsets.sub(() => this.updateViewportVars());
    viewport.contentSafeAreaInsets.sub(() => this.updateViewportVars());
  }

  private updateCssVars(theme: Record<string, string>) {
    const root = document.documentElement;
    Object.entries(theme).forEach(([key, value]) => {
      const cssVar = `--tg-theme-${key.replace(/_/g, '-')}`;
      root.style.setProperty(cssVar, value);
    });
  }

  private updateViewportVars() {
    const root = document.documentElement;

    // Stable height for layout
    root.style.setProperty('--app-height', `${viewport.height()}px`);
    root.style.setProperty('--stable-height', `${viewport.stableHeight()}px`);

    // Safe Areas from Viewport
    const insets = viewport.safeAreaInsets();
    if (insets) {
      root.style.setProperty('--safe-area-inset-top', `${insets.top}px`);
      root.style.setProperty('--safe-area-inset-bottom', `${insets.bottom}px`);
      root.style.setProperty('--safe-area-inset-left', `${insets.left}px`);
      root.style.setProperty('--safe-area-inset-right', `${insets.right}px`);
    }

    const contentInsets = viewport.contentSafeAreaInsets();
    if (contentInsets) {
      root.style.setProperty('--safe-area-content-inset-top', `${contentInsets.top}px`);
      root.style.setProperty('--safe-area-content-inset-bottom', `${contentInsets.bottom}px`);
      root.style.setProperty('--safe-area-content-inset-left', `${contentInsets.left}px`);
      root.style.setProperty('--safe-area-content-inset-right', `${contentInsets.right}px`);
    }
  }
}
